import type {
  RuntimeTileManifestV2,
  RuntimeWorldTilePayloadV2,
} from "../schema/runtimeTile.ts";
import {
  WORLD_TILE_SIZE_M,
  type WorldTileId,
} from "../schema/tile.ts";
import {
  selectDesiredTiles,
  type DesiredTile,
  type RuntimeTileLod,
  type TileLodRings,
} from "./lod.ts";
import type { RuntimeTileInstanceMetadata } from "./preparedMetadata.ts";

type ManifestTile = RuntimeTileManifestV2["tiles"][number];

export type TileLifecycleState = "queued" | "loading" | "ready" | "error";
export type TileDisposalReason =
  | "cache-eviction"
  | "lod-replacement"
  | "stale-response"
  | "controller-dispose";

export interface TileLoadRequest {
  entry: Readonly<ManifestTile>;
  lod: RuntimeTileLod;
  signal: AbortSignal;
}

export interface LoadedTile {
  payload: RuntimeWorldTilePayloadV2;
  /** Parsed payload size, not GPU allocation size. */
  sizeBytes: number;
  /** Optional serializable metadata prepared by the tile worker. */
  instances?: RuntimeTileInstanceMetadata;
}

export interface RuntimeTileSource {
  load(request: Readonly<TileLoadRequest>): Promise<LoadedTile>;
}

export interface TileStreamingSelectionProfile {
  maxCachedTiles: number;
  /** Promote already-selected tiles whose bounds approach the player. */
  fullDetailRadiusM: number;
  lodRings: Readonly<TileLodRings>;
}

export interface TileStreamingConfig extends TileStreamingSelectionProfile {
  maxConcurrentLoads: number;
}

export interface TileStreamingControllerOptions {
  manifest: Readonly<RuntimeTileManifestV2>;
  source: RuntimeTileSource;
  config: Readonly<TileStreamingConfig>;
  onEvent?: TileStreamingListener;
}

export interface ReadyTileSnapshot {
  tileId: WorldTileId;
  lod: RuntimeTileLod;
  payload: RuntimeWorldTilePayloadV2;
  sizeBytes: number;
  references: number;
  instances?: RuntimeTileInstanceMetadata;
}

export interface TileLease {
  tileId: WorldTileId;
  lod: RuntimeTileLod;
  payload: RuntimeWorldTilePayloadV2;
  release(): void;
}

export type TileStreamingEvent =
  | {
      type: "state";
      tileId: WorldTileId;
      lod: RuntimeTileLod;
      from: TileLifecycleState | "unloaded";
      to: TileLifecycleState | "unloaded";
    }
  | {
      type: "error";
      tileId: WorldTileId;
      lod: RuntimeTileLod;
      error: unknown;
    }
  | {
      type: "dispose";
      tileId: WorldTileId;
      lod: RuntimeTileLod;
      payload: RuntimeWorldTilePayloadV2;
      reason: TileDisposalReason;
    };

export type TileStreamingListener = (event: TileStreamingEvent) => void;

export interface TileStreamingMetrics {
  desiredTiles: number;
  queuedTiles: number;
  loadingTiles: number;
  readyTiles: number;
  errorTiles: number;
  readyByLod: Readonly<Record<RuntimeTileLod, number>>;
  retainedTiles: number;
  pinnedOutsideDesired: number;
  readyBytes: number;
  preparedBuildingItems: number;
  overCapacityBy: number;
  activeLoads: number;
  maxObservedConcurrentLoads: number;
  cacheHits: number;
  cacheMisses: number;
  loadsStarted: number;
  loadsCompleted: number;
  loadsAborted: number;
  loadsFailed: number;
  evictions: number;
  staleResponses: number;
  disposedPayloads: number;
}

interface TileRecord {
  entry: ManifestTile;
  state: TileLifecycleState;
  lod: RuntimeTileLod;
  desiredLod?: RuntimeTileLod;
  distanceTiles: number;
  sequence: number;
  token: number;
  abort?: AbortController;
  payload?: RuntimeWorldTilePayloadV2;
  instances?: RuntimeTileInstanceMetadata;
  sizeBytes: number;
  references: number;
  lastUsed: number;
  pendingDisposal?: TileDisposalReason;
}

interface MetricCounters {
  maxObservedConcurrentLoads: number;
  cacheHits: number;
  cacheMisses: number;
  loadsStarted: number;
  loadsCompleted: number;
  loadsAborted: number;
  loadsFailed: number;
  evictions: number;
  staleResponses: number;
  disposedPayloads: number;
}

function assertPositiveInteger(value: number, name: string): void {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error(`${name} must be a positive integer`);
  }
}

function validateSelectionProfile(
  manifest: Readonly<RuntimeTileManifestV2>,
  profile: Readonly<TileStreamingSelectionProfile>,
): void {
  assertPositiveInteger(profile.maxCachedTiles, "maxCachedTiles");
  if (
    !Number.isFinite(profile.fullDetailRadiusM) ||
    profile.fullDetailRadiusM < 0
  ) {
    throw new Error("fullDetailRadiusM must be a finite non-negative number");
  }
  // selectDesiredTiles performs the shared ring validation.
  selectDesiredTiles(manifest, { x: 0, z: 0 }, profile.lodRings);
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

export class TileStreamingController {
  readonly #manifest: Readonly<RuntimeTileManifestV2>;
  readonly #source: RuntimeTileSource;
  readonly #config: Readonly<TileStreamingConfig>;
  readonly #entries = new Map<WorldTileId, ManifestTile>();
  readonly #records = new Map<WorldTileId, TileRecord>();
  readonly #desired = new Map<WorldTileId, DesiredTile>();
  readonly #listeners = new Set<TileStreamingListener>();
  readonly #settleWaiters = new Set<() => void>();
  readonly #counters: MetricCounters = {
    maxObservedConcurrentLoads: 0,
    cacheHits: 0,
    cacheMisses: 0,
    loadsStarted: 0,
    loadsCompleted: 0,
    loadsAborted: 0,
    loadsFailed: 0,
    evictions: 0,
    staleResponses: 0,
    disposedPayloads: 0,
  };
  #activeLoads = 0;
  #sequence = 0;
  #clock = 0;
  #activeMaxCachedTiles: number;
  #disposed = false;

  constructor(options: Readonly<TileStreamingControllerOptions>) {
    assertPositiveInteger(
      options.config.maxConcurrentLoads,
      "maxConcurrentLoads",
    );
    validateSelectionProfile(options.manifest, options.config);

    this.#manifest = options.manifest;
    this.#source = options.source;
    this.#config = options.config;
    this.#activeMaxCachedTiles = options.config.maxCachedTiles;
    for (const entry of options.manifest.tiles) {
      if (this.#entries.has(entry.id)) {
        throw new Error(`Duplicate tile manifest entry: ${entry.id}`);
      }
      this.#entries.set(entry.id, entry);
    }
    if (options.onEvent !== undefined) {
      this.#listeners.add(options.onEvent);
    }
  }

  update(
    player: Readonly<{ x: number; z: number }>,
    profile: Readonly<TileStreamingSelectionProfile> = this.#config,
  ): readonly DesiredTile[] {
    this.#assertUsable();
    if (!Number.isFinite(player.x) || !Number.isFinite(player.z)) {
      throw new Error("Player coordinates must be finite");
    }
    validateSelectionProfile(this.#manifest, profile);
    const desired = selectDesiredTiles(
      this.#manifest,
      {
        x: Math.floor(player.x / WORLD_TILE_SIZE_M),
        z: Math.floor(player.z / WORLD_TILE_SIZE_M),
      },
      profile.lodRings,
      {
        focus: player,
        radiusM: profile.fullDetailRadiusM,
      },
    );
    if (desired.length > profile.maxCachedTiles) {
      throw new Error(
        `LOD rings select ${desired.length} tiles, exceeding maxCachedTiles=${profile.maxCachedTiles}`,
      );
    }
    this.#activeMaxCachedTiles = profile.maxCachedTiles;

    this.#desired.clear();
    for (const tile of desired) {
      this.#desired.set(tile.tileId, tile);
    }

    for (const [tileId, record] of this.#records) {
      const target = this.#desired.get(tileId);
      if (target === undefined) {
        this.#makeUndesired(record);
        continue;
      }
      record.distanceTiles = target.distanceTiles;
      record.desiredLod = target.lod;
      if (record.lod !== target.lod) {
        if (
          record.state === "ready" &&
          record.payload !== undefined &&
          record.references === 0
        ) {
          // Every schema-v2 file contains all three visual LODs. Keep the
          // verified payload resident and let the renderer rebuild only its
          // lightweight view instead of refetching, reparsing, and cloning the
          // same JSON whenever a tile changes rings.
          record.lod = target.lod;
          record.lastUsed = ++this.#clock;
          delete record.instances;
          this.#counters.cacheHits += 1;
          this.#emitState(record, "ready", "ready");
        } else {
          this.#replaceLod(record, target.lod);
        }
      } else if (record.state === "ready") {
        delete record.pendingDisposal;
        record.lastUsed = ++this.#clock;
        this.#counters.cacheHits += 1;
      } else if (record.state === "error") {
        this.#queue(record, target.lod);
      }
    }

    for (const target of desired) {
      if (this.#records.has(target.tileId)) {
        continue;
      }
      const entry = this.#entries.get(target.tileId);
      if (entry === undefined) {
        continue;
      }
      this.#counters.cacheMisses += 1;
      const record: TileRecord = {
        entry,
        state: "queued",
        lod: target.lod,
        desiredLod: target.lod,
        distanceTiles: target.distanceTiles,
        sequence: ++this.#sequence,
        token: 0,
        sizeBytes: 0,
        references: 0,
        lastUsed: ++this.#clock,
      };
      this.#records.set(target.tileId, record);
      this.#emitState(record, "unloaded", "queued");
    }

    this.#evictIfNeeded();
    this.#pump();
    this.#resolveSettled();
    return desired;
  }

  settle(): Promise<void> {
    if (this.#activeLoads === 0 && !this.#hasQueued()) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.#settleWaiters.add(resolve);
    });
  }

  getReadyTile(tileId: WorldTileId): ReadyTileSnapshot | undefined {
    const record = this.#records.get(tileId);
    if (record?.state !== "ready" || record.payload === undefined) {
      return undefined;
    }
    record.lastUsed = ++this.#clock;
    return this.#readySnapshot(record, record.payload);
  }

  getReadyTiles(): readonly ReadyTileSnapshot[] {
    return [...this.#records.values()]
      .filter(
        (record): record is TileRecord & {
          state: "ready";
          payload: RuntimeWorldTilePayloadV2;
        } => record.state === "ready" && record.payload !== undefined,
      )
      .sort((left, right) => left.entry.id.localeCompare(right.entry.id))
      .map((record) => this.#readySnapshot(record, record.payload));
  }

  acquireTile(tileId: WorldTileId): TileLease | undefined {
    const record = this.#records.get(tileId);
    if (
      this.#disposed ||
      record?.state !== "ready" ||
      record.payload === undefined
    ) {
      return undefined;
    }
    record.references += 1;
    record.lastUsed = ++this.#clock;
    let released = false;
    const payload = record.payload;
    const lod = record.lod;
    return {
      tileId,
      lod,
      payload,
      release: () => {
        if (released) {
          return;
        }
        released = true;
        record.references -= 1;
        if (record.references < 0) {
          throw new Error(`Tile lease underflow: ${tileId}`);
        }
        if (record.references === 0) {
          this.#finishDeferredDisposal(record);
          this.#evictIfNeeded();
          this.#pump();
          this.#resolveSettled();
        }
      },
    };
  }

  subscribe(listener: TileStreamingListener): () => void {
    this.#assertUsable();
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  metrics(): TileStreamingMetrics {
    const stateCounts: Record<TileLifecycleState, number> = {
      queued: 0,
      loading: 0,
      ready: 0,
      error: 0,
    };
    const readyByLod: Record<RuntimeTileLod, number> = { 0: 0, 1: 0, 2: 0 };
    let retainedTiles = 0;
    let pinnedOutsideDesired = 0;
    let readyBytes = 0;
    let preparedBuildingItems = 0;
    for (const record of this.#records.values()) {
      stateCounts[record.state] += 1;
      if (record.state === "ready") {
        readyByLod[record.lod] += 1;
        readyBytes += record.sizeBytes;
        preparedBuildingItems += record.instances?.buildings.items.length ?? 0;
      }
      if (record.references > 0) {
        retainedTiles += 1;
        if (!this.#desired.has(record.entry.id)) {
          pinnedOutsideDesired += 1;
        }
      }
    }
    return {
      desiredTiles: this.#desired.size,
      queuedTiles: stateCounts.queued,
      loadingTiles: stateCounts.loading,
      readyTiles: stateCounts.ready,
      errorTiles: stateCounts.error,
      readyByLod,
      retainedTiles,
      pinnedOutsideDesired,
      readyBytes,
      preparedBuildingItems,
      overCapacityBy: Math.max(
        0,
        stateCounts.ready - this.#activeMaxCachedTiles,
      ),
      activeLoads: this.#activeLoads,
      ...this.#counters,
    };
  }

  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.#desired.clear();
    for (const record of [...this.#records.values()]) {
      if (record.state === "loading") {
        record.abort?.abort();
        record.token += 1;
        this.#counters.loadsAborted += 1;
        this.#records.delete(record.entry.id);
        this.#emitState(record, "loading", "unloaded");
      } else if (record.state === "ready" && record.payload !== undefined) {
        if (record.references > 0) {
          record.pendingDisposal = "controller-dispose";
        } else {
          this.#disposeReady(record, "controller-dispose");
        }
      } else {
        this.#records.delete(record.entry.id);
        this.#emitState(record, record.state, "unloaded");
      }
    }
    this.#resolveSettled();
  }

  #makeUndesired(record: TileRecord): void {
    delete record.desiredLod;
    if (record.state === "queued" || record.state === "error") {
      this.#records.delete(record.entry.id);
      this.#emitState(record, record.state, "unloaded");
    } else if (record.state === "loading") {
      record.abort?.abort();
      record.token += 1;
      this.#counters.loadsAborted += 1;
      this.#records.delete(record.entry.id);
      this.#emitState(record, "loading", "unloaded");
    }
  }

  #replaceLod(record: TileRecord, lod: RuntimeTileLod): void {
    if (record.state === "loading") {
      record.abort?.abort();
      record.token += 1;
      this.#counters.loadsAborted += 1;
      this.#queue(record, lod);
    } else if (record.state === "queued" || record.state === "error") {
      this.#queue(record, lod);
    } else if (record.references > 0) {
      record.pendingDisposal = "lod-replacement";
    } else {
      this.#disposeReady(record, "lod-replacement", false);
      this.#queue(record, lod);
    }
  }

  #queue(record: TileRecord, lod: RuntimeTileLod): void {
    const from = record.state;
    record.state = "queued";
    record.lod = lod;
    record.sequence = ++this.#sequence;
    delete record.abort;
    delete record.payload;
    delete record.instances;
    record.sizeBytes = 0;
    delete record.pendingDisposal;
    if (from !== "queued") {
      this.#emitState(record, from, "queued");
    }
  }

  #pump(): void {
    if (this.#disposed) {
      return;
    }
    while (this.#activeLoads < this.#config.maxConcurrentLoads) {
      const next = [...this.#records.values()]
        .filter((record) => record.state === "queued")
        .sort(
          (left, right) =>
            left.distanceTiles - right.distanceTiles ||
            left.lod - right.lod ||
            left.sequence - right.sequence,
        )[0];
      if (next === undefined) {
        break;
      }
      this.#startLoad(next);
    }
  }

  #startLoad(record: TileRecord): void {
    const token = ++record.token;
    const requestedLod = record.lod;
    const abort = new AbortController();
    record.abort = abort;
    record.state = "loading";
    this.#activeLoads += 1;
    this.#counters.loadsStarted += 1;
    this.#counters.maxObservedConcurrentLoads = Math.max(
      this.#counters.maxObservedConcurrentLoads,
      this.#activeLoads,
    );
    this.#emitState(record, "queued", "loading");

    void this.#source
      .load({ entry: record.entry, lod: requestedLod, signal: abort.signal })
      .then((loaded) => {
        const current = this.#records.get(record.entry.id);
        if (
          this.#disposed ||
          current !== record ||
          record.token !== token ||
          record.state !== "loading"
        ) {
          this.#counters.staleResponses += 1;
          this.#emitDisposal(
            record.entry.id,
            requestedLod,
            loaded.payload,
            "stale-response",
          );
          return;
        }
        if (!Number.isFinite(loaded.sizeBytes) || loaded.sizeBytes < 0) {
          throw new Error(`Invalid loaded byte size for tile ${record.entry.id}`);
        }
        record.state = "ready";
        delete record.abort;
        record.payload = loaded.payload;
        if (loaded.instances === undefined) {
          delete record.instances;
        } else {
          record.instances = loaded.instances;
        }
        record.sizeBytes = loaded.sizeBytes;
        record.lastUsed = ++this.#clock;
        this.#counters.loadsCompleted += 1;
        this.#emitState(record, "loading", "ready");
        if (!this.#desired.has(record.entry.id)) {
          this.#evictIfNeeded(true);
        } else {
          this.#evictIfNeeded();
        }
      })
      .catch((error: unknown) => {
        const current = this.#records.get(record.entry.id);
        if (
          current !== record ||
          record.token !== token ||
          record.state !== "loading"
        ) {
          return;
        }
        delete record.abort;
        if (isAbortError(error) || abort.signal.aborted) {
          this.#records.delete(record.entry.id);
          this.#emitState(record, "loading", "unloaded");
          return;
        }
        record.state = "error";
        this.#counters.loadsFailed += 1;
        this.#emitState(record, "loading", "error");
        this.#emit({
          type: "error",
          tileId: record.entry.id,
          lod: record.lod,
          error,
        });
      })
      .finally(() => {
        this.#activeLoads -= 1;
        this.#pump();
        this.#resolveSettled();
      });
  }

  #finishDeferredDisposal(record: TileRecord): void {
    const reason = record.pendingDisposal;
    if (reason === undefined || record.payload === undefined) {
      return;
    }
    const nextLod = record.desiredLod;
    const shouldQueue = !this.#disposed && nextLod !== undefined;
    this.#disposeReady(record, reason, !shouldQueue);
    if (shouldQueue) {
      this.#queue(record, nextLod);
    }
  }

  #evictIfNeeded(forceUndesired = false): void {
    while (true) {
      const readyCount = [...this.#records.values()].filter(
        (record) => record.state === "ready",
      ).length;
      if (!forceUndesired && readyCount <= this.#activeMaxCachedTiles) {
        return;
      }
      const candidate = [...this.#records.values()]
        .filter(
          (record) =>
            record.state === "ready" &&
            record.references === 0 &&
            !this.#desired.has(record.entry.id),
        )
        .sort((left, right) => left.lastUsed - right.lastUsed)[0];
      if (candidate === undefined) {
        return;
      }
      this.#counters.evictions += 1;
      this.#disposeReady(candidate, "cache-eviction");
      forceUndesired = false;
    }
  }

  #disposeReady(
    record: TileRecord,
    reason: TileDisposalReason,
    remove = true,
  ): void {
    const payload = record.payload;
    if (payload === undefined) {
      return;
    }
    delete record.payload;
    delete record.instances;
    record.sizeBytes = 0;
    delete record.pendingDisposal;
    if (remove) {
      this.#records.delete(record.entry.id);
    }
    if (remove) {
      this.#emitState(record, "ready", "unloaded");
    }
    this.#emitDisposal(record.entry.id, record.lod, payload, reason);
  }

  #readySnapshot(
    record: TileRecord,
    payload: RuntimeWorldTilePayloadV2,
  ): ReadyTileSnapshot {
    return {
      tileId: record.entry.id,
      lod: record.lod,
      payload,
      sizeBytes: record.sizeBytes,
      references: record.references,
      ...(record.instances === undefined
        ? {}
        : { instances: record.instances }),
    };
  }

  #emitState(
    record: TileRecord,
    from: TileLifecycleState | "unloaded",
    to: TileLifecycleState | "unloaded",
  ): void {
    this.#emit({
      type: "state",
      tileId: record.entry.id,
      lod: record.lod,
      from,
      to,
    });
  }

  #emitDisposal(
    tileId: WorldTileId,
    lod: RuntimeTileLod,
    payload: RuntimeWorldTilePayloadV2,
    reason: TileDisposalReason,
  ): void {
    this.#counters.disposedPayloads += 1;
    this.#emit({ type: "dispose", tileId, lod, payload, reason });
  }

  #emit(event: TileStreamingEvent): void {
    for (const listener of this.#listeners) {
      listener(event);
    }
  }

  #hasQueued(): boolean {
    return [...this.#records.values()].some(
      (record) => record.state === "queued",
    );
  }

  #resolveSettled(): void {
    if (this.#activeLoads !== 0 || this.#hasQueued()) {
      return;
    }
    for (const resolve of this.#settleWaiters) {
      resolve();
    }
    this.#settleWaiters.clear();
  }

  #assertUsable(): void {
    if (this.#disposed) {
      throw new Error("TileStreamingController has been disposed");
    }
  }
}
